import { streamingDebugProps } from "@/lib/streaming-debug"
import { assertPresent } from "@/lib/utils/assertions"
import { wait } from "./promise-utils"

export interface EventSmoother {
  add(deliver: () => boolean | void, delayable: boolean): void
  done(): void
  cancel(): void
  flushAll(): void
}

// A gizmo that smoooths out a flow of events that arrive
// inconsistently spaced in time.
export class TimedEventSmoother implements EventSmoother {
  static readonly DEFAULT_DELAY = 80
  static readonly DEFAULT_PRIMING_THRESHOLD = 20
  running = false
  priming = true
  streamDone = false
  queue: Array<QueueEntry> = []
  delay: number = TimedEventSmoother.DEFAULT_DELAY

  // Add an item to the stream by calling add(). Provide a function
  // that will be used to deliver the item later.
  //
  // The caller can declare whether this event is delayable (typically
  // user-visible events like text would be delayable and meta info
  // might not be).
  add(deliver: () => void, delayable = true) {
    // Non-delayable events (like UI signal skeletons) should be delivered immediately
    // to force React renders and avoid batching with subsequent events.
    // But when debug-paused, queue them so the delivery loop's pause check applies.
    if (!delayable && !streamingDebugProps.stopStream) {
      deliver()
      return
    }

    this.queue.push({ deliver, delayable })
    this.streamDone = false
    void this.startDeliveryLoop()
  }

  done() {
    this.priming = false
    this.streamDone = true
    void this.startDeliveryLoop() // if not already started
  }

  // Stop processing the queue and clear it.
  cancel() {
    this.queue = []
    this.running = false
    this.priming = true
    this.delay = TimedEventSmoother.DEFAULT_DELAY
  }

  // Immediately flush all queued events synchronously.
  // Used before processing position-dependent events to ensure text is rendered first.
  // Skipped when debug-paused so the delivery loop's pause check stays in control.
  flushAll() {
    if (streamingDebugProps.stopStream) return

    while (this.queue.length) {
      const entry = this.queue.shift()
      if (entry) {
        entry.deliver()
      }
    }
  }

  private async startDeliveryLoop() {
    // Are we already running the loop?
    if (this.running) return

    // No loop is running. Before starting it, let's see if we need
    // to prime the buffer by letting the queue fill with delayable
    // entries. If the next few entries are not delayable, we let
    // them go out regardless. (This is helpful when the stream starts
    // with some meta data, like destination or delay info, that we
    // want to send right away.)
    this.flushNondelayableEntries()
    if (
      this.priming &&
      this.queue.length < TimedEventSmoother.DEFAULT_PRIMING_THRESHOLD
    ) {
      // keep priming
      return
    }

    // Done priming. Ready to run the loop.
    this.priming = false
    this.running = true

    try {
      while (this.queue.length) {
        if (this.queue[0].delayable) {
          await wait(this.delay)

          if (streamingDebugProps.stopStream) {
            await streamingDebugProps.nextTokenPromise?.promise
          }
        }
        const entry = this.queue.shift()
        if (!entry) return
        entry.deliver()
        if (!entry.delayable) {
          continue
        }

        // We may need to tune this curve, but the general idea is to
        // deliver events a little slower than the median elapsed time
        // between event arrivals so that we can smooth over the longer
        // gaps. Speed up if the queue gets long.
        const count = Math.max(2, this.queue.length)
        const newDelay = this.streamDone
          ? 0
          : Math.floor(100 * Math.exp(-0.2 * count))

        // Don't let the delay change too much between iterations.
        const delta = Math.max(-10, Math.min(40, newDelay - this.delay))
        this.delay += delta
      }
    } finally {
      this.running = false
    }
  }

  private flushNondelayableEntries() {
    while (this.queue.length && !this.queue[0].delayable) {
      const entry = this.queue.shift()
      assertPresent(entry).deliver()
    }
  }
}

export class NullEventSmoother implements EventSmoother {
  add(deliver: () => boolean | void, _delayable: boolean) {
    deliver()
  }
  done() {}
  cancel() {}
  flushAll() {}
}

interface QueueEntry {
  deliver: () => void
  delayable: boolean
}
